import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom'; // Using Link for navigation buttons
import { collection, getDocs, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiTarget, FiFlag } from 'react-icons/fi';
import { db } from '../firebase'; // Adjust path if needed
import { useAuth } from '../context/AuthContext'; // Adjust path if needed
import { formatDate } from '../utils/styleUtils'; // Adjust path if needed
import styles from '../styles/CampaignListPage.module.css'; // Adjust path if needed

// Map a campaign status string to a badge color class (falls back to a neutral badge).
const statusClassFor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('active') || s.includes('ongoing') || s.includes('running')) return 'badgeActive';
    if (s.includes('complete') || s.includes('done') || s.includes('finished')) return 'badgeDone';
    if (s.includes('plan') || s.includes('upcoming') || s.includes('draft') || s.includes('schedul')) return 'badgePlanned';
    if (s.includes('paus') || s.includes('hold')) return 'badgePaused';
    if (s.includes('cancel') || s.includes('stop')) return 'badgeCancelled';
    return 'badgeDefault';
};

// Helper component to render sort indicators (▲/▼)
const SortIndicator = ({ direction }) => {
    if (!direction) return null; // Don't show if column isn't sorted
    // Using simple text arrows, replace with icons if preferred
    return <span className={styles.sortIndicator}>{direction === 'asc' ? ' ▲' : ' ▼'}</span>;
};

const CampaignListPage = () => {
    const [campaigns, setCampaigns] = useState([]); // Raw data from Firestore
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null); // Track deletion state for UI feedback
    const [search, setSearch] = useState('');
    const { userRole } = useAuth(); // Get current user's role for permissions

    // --- State for Sorting ---
    const [sortField, setSortField] = useState('endDate'); // Default sort field
    const [sortDirection, setSortDirection] = useState('asc'); // Default sort direction

    // --- Fetch Campaigns ---
    useEffect(() => {
        const fetchCampaigns = async () => {
            setLoading(true);
            setError('');
            try {
                const campaignsCol = collection(db, 'campaigns');
                // Fetching ordered by name initially, client-side sort will override display order
                const q = query(campaignsCol, orderBy('clientName', 'asc'));
                const querySnapshot = await getDocs(q);
                const campaignList = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    // Ensure dates are converted to JS Date objects
                    return {
                        id: doc.id,
                        ...data,
                        startDate: data.startDate?.toDate ? data.startDate.toDate() : null, // Safer date conversion
                        endDate: data.endDate?.toDate ? data.endDate.toDate() : null,     // Safer date conversion
                        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
                        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : null,
                    }
                });
                setCampaigns(campaignList);
            } catch (err) {
                console.error("Error fetching campaigns:", err);
                setError("Failed to load campaigns. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchCampaigns();
    }, []); // Fetch only once on component mount

    // --- Delete Handler ---
    const handleDeleteCampaign = async (campaignId, campaignDesc) => {
        if (userRole !== 'Manager') {
            setError("You do not have permission to delete campaigns.");
            return;
        }
        const shortDesc = campaignDesc?.substring(0, 30) || 'this campaign';
        if (window.confirm(`Are you sure you want to delete campaign "${shortDesc}..."?\nThis action cannot be undone.`)) {
            setDeletingId(campaignId);
            setError('');
            try {
                await deleteDoc(doc(db, 'campaigns', campaignId));
                setCampaigns(prevCampaigns => prevCampaigns.filter(c => c.id !== campaignId));
                console.log(`Campaign ${campaignId} deleted.`);
            } catch (err) {
                 console.error("Error deleting campaign:", err);
                 setError(`Failed to delete campaign. Please try again.`);
            } finally {
                 setDeletingId(null);
            }
        }
    };

    // --- Sorting Handler ---
     const handleSort = useCallback((field) => {
        setSortDirection(prevDirection =>
            sortField === field ? (prevDirection === 'asc' ? 'desc' : 'asc') : 'asc'
        );
        setSortField(field);
     }, [sortField]);

    // --- Calculate Sorted Campaigns using useMemo ---
    const sortedCampaigns = useMemo(() => {
        let campaignsToSort = [...campaigns];
        if (sortField) {
            campaignsToSort.sort((a, b) => {
                let valA, valB;
                switch (sortField) {
                    case 'clientName':
                        valA = a.clientName || ''; valB = b.clientName || ''; break;
                    case 'description':
                         valA = a.description || ''; valB = b.description || ''; break;
                    case 'status':
                        valA = a.status || ''; valB = b.status || ''; break;
                    case 'startDate':
                        valA = a.startDate instanceof Date ? a.startDate.getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                        valB = b.startDate instanceof Date ? b.startDate.getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                        return sortDirection === 'asc' ? valA - valB : valB - valA;
                    case 'endDate':
                        valA = a.endDate instanceof Date ? a.endDate.getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                        valB = b.endDate instanceof Date ? b.endDate.getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                        return sortDirection === 'asc' ? valA - valB : valB - valA;
                    default: return 0;
                }
                // Case-insensitive string comparison
                const comparison = (valA || '').toString().localeCompare((valB || '').toString(), undefined, { sensitivity: 'base' });
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }
        return campaignsToSort;
    }, [campaigns, sortField, sortDirection]);

    const canDelete = userRole === 'Manager';

    // --- Date-based stats ---
    const stats = useMemo(() => {
        const now = Date.now();
        const weekAhead = now + 7 * 24 * 60 * 60 * 1000;
        const s = { total: campaigns.length, ongoing: 0, endingSoon: 0, ended: 0 };
        campaigns.forEach((c) => {
            const start = c.startDate instanceof Date ? c.startDate.getTime() : null;
            const end = c.endDate instanceof Date ? c.endDate.getTime() : null;
            if (end !== null && end < now) { s.ended += 1; return; }
            if (start !== null && end !== null && start <= now && now <= end) s.ongoing += 1;
            if (end !== null && end >= now && end <= weekAhead) s.endingSoon += 1;
        });
        return s;
    }, [campaigns]);

    // --- Search filter (applied on top of the sorted list) ---
    const filteredCampaigns = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return sortedCampaigns;
        return sortedCampaigns.filter((c) => {
            const hay = `${c.clientName || ''} ${c.description || ''} ${c.status || ''}`.toLowerCase();
            return hay.includes(term);
        });
    }, [sortedCampaigns, search]);

    // --- Render JSX ---
    return (
        <div className={styles.pageContainer}>
            {/* Page Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.title}>Campaigns</h2>
                    <p className={styles.subtitle}>Plan and track client marketing campaigns.</p>
                </div>
                <Link to="/campaigns/new" className={styles.addButton}>
                    <FiPlus /> Create Campaign
                </Link>
            </div>

            {/* Display any errors */}
            {error && <div className={styles.errorMessage}>{error}</div>}

            {/* Stats */}
            <div className={styles.statsRow}>
                <div className={`${styles.statCard} ${styles.statTotal}`}>
                    <div className={styles.statLabel}>Total</div>
                    <div className={styles.statValue}>{stats.total}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statOngoing}`}>
                    <div className={styles.statLabel}>Ongoing</div>
                    <div className={styles.statValue}>{stats.ongoing}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statSoon}`}>
                    <div className={styles.statLabel}>Ending this week</div>
                    <div className={styles.statValue}>{stats.endingSoon}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statEnded}`}>
                    <div className={styles.statLabel}>Ended</div>
                    <div className={styles.statValue}>{stats.ended}</div>
                </div>
            </div>

            {/* Toolbar */}
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <FiSearch className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search by client, description or status…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
            </div>

            {/* Loading Indicator */}
            {loading ? (
                <div className={styles.loadingIndicator}>Loading campaigns...</div>
            ) : campaigns.length === 0 ? (
                <div className={styles.emptyState}>
                    <FiFlag className={styles.emptyIcon} />
                    <p>No campaigns yet.</p>
                    <span>Click “Create Campaign” to start one.</span>
                </div>
            ) : filteredCampaigns.length === 0 ? (
                <div className={styles.emptyState}>
                    <FiSearch className={styles.emptyIcon} />
                    <p>No campaigns match your search.</p>
                </div>
            ) : (
                /* Table Container */
                <div className={styles.tableContainer}>
                    <table className={styles.campaignTable}>
                        {/* Table Header - Hidden on mobile */}
                        <thead className={styles.tableHeader}>
                            <tr>
                                <th onClick={() => handleSort('clientName')} className={styles.sortableHeader}>
                                    Client
                                    {sortField === 'clientName' && <SortIndicator direction={sortDirection} />}
                                </th>
                                <th onClick={() => handleSort('description')} className={`${styles.descriptionColumn} ${styles.sortableHeader}`}>
                                    Description
                                     {sortField === 'description' && <SortIndicator direction={sortDirection} />}
                                </th>
                                <th onClick={() => handleSort('startDate')} className={`${styles.dateColumn} ${styles.sortableHeader}`}>
                                    Start Date
                                     {sortField === 'startDate' && <SortIndicator direction={sortDirection} />}
                                </th>
                                <th onClick={() => handleSort('endDate')} className={`${styles.dateColumn} ${styles.sortableHeader}`}>
                                    End Date
                                    {sortField === 'endDate' && <SortIndicator direction={sortDirection} />}
                                </th>
                                <th onClick={() => handleSort('status')} className={styles.sortableHeader}>
                                    Status
                                    {sortField === 'status' && <SortIndicator direction={sortDirection} />}
                                </th>
                                <th className={styles.actionsHeader}>Actions</th>
                            </tr>
                        </thead>
                        {/* Table Body */}
                        <tbody className={styles.tableBody}>
                            {filteredCampaigns.map(campaign => (
                                <tr key={campaign.id} className={deletingId === campaign.id ? styles.deletingRow : ''}>
                                    <td data-label="Client">
                                        <span className={styles.clientName}>{campaign.clientName || 'N/A'}</span>
                                    </td>
                                    <td data-label="Description" className={styles.descriptionColumn} title={campaign.description}>
                                        {campaign.description || <span className={styles.muted}>—</span>}
                                    </td>
                                    <td data-label="Start Date" className={styles.dateColumn}>{formatDate(campaign.startDate) || <span className={styles.muted}>—</span>}</td>
                                    <td data-label="End Date" className={styles.dateColumn}>{formatDate(campaign.endDate) || <span className={styles.muted}>—</span>}</td>
                                    <td data-label="Status">
                                        {campaign.status
                                            ? <span className={`${styles.statusBadge} ${styles[statusClassFor(campaign.status)]}`}>{campaign.status}</span>
                                            : <span className={styles.muted}>—</span>}
                                    </td>
                                    <td data-label="Actions" className={styles.actionsCell}>
                                        <Link
                                            to={`/campaigns/${campaign.id}/plan`}
                                            className={`${styles.actionButton} ${styles.planButton}`}
                                            title="Plan Campaign"
                                        >
                                            <FiTarget />
                                        </Link>
                                        <Link
                                            to={`/campaigns/${campaign.id}/edit`}
                                            className={`${styles.actionButton} ${styles.editButton}`}
                                            title="Edit Campaign"
                                        >
                                            <FiEdit2 />
                                        </Link>
                                        {canDelete && (
                                            <button
                                                onClick={() => handleDeleteCampaign(campaign.id, campaign.description)}
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                disabled={deletingId === campaign.id}
                                                title="Delete Campaign"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CampaignListPage;
